import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Brain, CheckCircle2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAllModules } from "../content";
import { markCompleted } from "../progress-store";

// ── Placement test questions ──
// Each level has 2 questions; 10 total (levels 1-5, with levels 6-7 mapped to 5 & 7)

interface PlacementQuestion {
  level: number;
  question: { en: string; es: string };
  options: { en: string; es: string }[];
  correctIndex: number;
}

const QUESTIONS: PlacementQuestion[] = [
  {
    level: 1,
    question: {
      en: "Which hand wins: Flush or Straight?",
      es: "\u00bfQu\u00e9 mano gana: Flush o Escalera?",
    },
    options: [
      { en: "Straight", es: "Escalera" },
      { en: "Flush", es: "Flush (Color)" },
      { en: "They tie", es: "Empatan" },
      { en: "Depends on the cards", es: "Depende de las cartas" },
    ],
    correctIndex: 1,
  },
  {
    level: 1,
    question: {
      en: "What does BTN mean?",
      es: "\u00bfQu\u00e9 significa BTN?",
    },
    options: [
      { en: "Big Tight Number", es: "Big Tight Number" },
      { en: "Button (dealer position)", es: "Button (posici\u00f3n del dealer)" },
      { en: "Best Turn Next", es: "Best Turn Next" },
      { en: "Bet The Nuts", es: "Bet The Nuts" },
    ],
    correctIndex: 1,
  },
  {
    level: 2,
    question: {
      en: "You have 72o UTG. Best play?",
      es: "Tienes 72o en UTG. \u00bfMejor jugada?",
    },
    options: [
      { en: "Raise", es: "Subir" },
      { en: "Call", es: "Igualar" },
      { en: "Fold", es: "Retirarse" },
      { en: "All-in", es: "All-in" },
    ],
    correctIndex: 2,
  },
  {
    level: 2,
    question: {
      en: "Why is late position better?",
      es: "\u00bfPor qu\u00e9 es mejor la posici\u00f3n tard\u00eda?",
    },
    options: [
      { en: "You get better cards", es: "Te tocan mejores cartas" },
      { en: "You act after opponents", es: "Act\u00faas despu\u00e9s de tus oponentes" },
      { en: "The blinds are cheaper", es: "Las ciegas son m\u00e1s baratas" },
      { en: "You can see the flop first", es: "Puedes ver el flop primero" },
    ],
    correctIndex: 1,
  },
  {
    level: 3,
    question: {
      en: "What is a continuation bet?",
      es: "\u00bfQu\u00e9 es un continuation bet?",
    },
    options: [
      { en: "Calling after someone bets", es: "Igualar despu\u00e9s de que alguien apueste" },
      { en: "Betting flop after raising preflop", es: "Apostar en el flop tras subir preflop" },
      { en: "Going all-in on the turn", es: "Ir all-in en el turn" },
      { en: "Checking twice in a row", es: "Pasar dos veces seguidas" },
    ],
    correctIndex: 1,
  },
  {
    level: 3,
    question: {
      en: "What does TAG mean?",
      es: "\u00bfQu\u00e9 significa TAG?",
    },
    options: [
      { en: "Total Aggressive Game", es: "Total Aggressive Game" },
      { en: "Tight-Aggressive", es: "Tight-Aggressive" },
      { en: "Table Average Gain", es: "Table Average Gain" },
      { en: "Turn Action Guide", es: "Turn Action Guide" },
    ],
    correctIndex: 1,
  },
  {
    level: 4,
    question: {
      en: "A flush draw has how many outs?",
      es: "\u00bfCu\u00e1ntos outs tiene un proyecto de color?",
    },
    options: [
      { en: "4", es: "4" },
      { en: "8", es: "8" },
      { en: "9", es: "9" },
      { en: "13", es: "13" },
    ],
    correctIndex: 2,
  },
  {
    level: 4,
    question: {
      en: "Pot is 100, opponent bets 50. What pot odds do you get?",
      es: "El bote es 100, tu oponente apuesta 50. \u00bfQu\u00e9 pot odds tienes?",
    },
    options: [
      { en: "2:1", es: "2:1" },
      { en: "3:1", es: "3:1" },
      { en: "4:1", es: "4:1" },
      { en: "1.5:1", es: "1.5:1" },
    ],
    correctIndex: 1,
  },
  {
    level: 5,
    question: {
      en: "On a K\u2660 7\u2666 2\u2663 board, this is a...",
      es: "En un board K\u2660 7\u2666 2\u2663, esto es un...",
    },
    options: [
      { en: "Wet board", es: "Board mojado" },
      { en: "Dry board", es: "Board seco" },
      { en: "Coordinated board", es: "Board coordinado" },
      { en: "Draw-heavy board", es: "Board con muchos proyectos" },
    ],
    correctIndex: 1,
  },
  {
    level: 7,
    question: {
      en: "What does ICM stand for?",
      es: "\u00bfQu\u00e9 significa ICM?",
    },
    options: [
      { en: "In-game Chip Management", es: "In-game Chip Management" },
      { en: "Independent Chip Model", es: "Independent Chip Model" },
      { en: "Incremental Card Method", es: "Incremental Card Method" },
      { en: "Initial Calling Metric", es: "Initial Calling Metric" },
    ],
    correctIndex: 1,
  },
];

interface PlacementTestProps {
  onComplete: (recommendedLevel: number) => void;
  onSkip: () => void;
}

export const PlacementTest = ({ onComplete, onSkip }: PlacementTestProps) => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";

  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(
    () => new Array(QUESTIONS.length).fill(null) as (number | null)[],
  );
  const [phase, setPhase] = useState<"intro" | "test" | "result">("intro");
  const [recommendedLevel, setRecommendedLevel] = useState(1);

  const handleAnswer = useCallback(
    (optionIndex: number) => {
      const newAnswers = [...answers];
      newAnswers[currentQ] = optionIndex;
      setAnswers(newAnswers);

      // Auto-advance after a small delay
      setTimeout(() => {
        if (currentQ < QUESTIONS.length - 1) {
          setCurrentQ(currentQ + 1);
        } else {
          // Calculate result
          const level = calculateRecommendedLevel(newAnswers);
          setRecommendedLevel(level);
          setPhase("result");
        }
      }, 300);
    },
    [answers, currentQ],
  );

  const calculateRecommendedLevel = (ans: (number | null)[]): number => {
    // Group questions by level and check if both are correct
    const levelResults = new Map<number, boolean>();
    const levelQuestions = new Map<number, { correct: boolean }[]>();

    for (let i = 0; i < QUESTIONS.length; i++) {
      const q = QUESTIONS[i];
      const isCorrect = ans[i] === q.correctIndex;
      if (!levelQuestions.has(q.level)) {
        levelQuestions.set(q.level, []);
      }
      levelQuestions.get(q.level)!.push({ correct: isCorrect });
    }

    for (const [level, results] of levelQuestions) {
      levelResults.set(level, results.every((r) => r.correct));
    }

    // Recommended level = highest level where both questions were correct, + 1
    let highestPassed = 0;
    const sortedLevels = [...levelResults.entries()].sort(
      (a, b) => a[0] - b[0],
    );

    for (const [level, passed] of sortedLevels) {
      if (passed) {
        highestPassed = level;
      } else {
        // Stop at first failure for a contiguous progression
        break;
      }
    }

    return Math.min(highestPassed + 1, 7);
  };

  const handleStartAtLevel = (level: number) => {
    // Mark all lessons below the recommended level as "tested out"
    const modules = getAllModules();
    for (const mod of modules) {
      const moduleLevel = parseInt(mod.id, 10);
      if (moduleLevel < level) {
        for (const lesson of mod.lessons) {
          markCompleted(lesson.id, 0, 1);
        }
      }
    }
    onComplete(level);
  };

  const progressPercent = ((currentQ + 1) / QUESTIONS.length) * 100;

  // ── Intro screen ──
  if (phase === "intro") {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-secondary/20 border-2 border-secondary flex items-center justify-center mx-auto">
            <Brain className="w-10 h-10 text-secondary" />
          </div>

          <div>
            <h1 className="text-2xl font-bold mb-2">
              {t("placement_title")}
            </h1>
            <p className="text-muted-foreground">
              {t("placement_subtitle")}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold h-12"
              onClick={() => setPhase("test")}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {lang === "es" ? "Comenzar" : "Start"}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={onSkip}
            >
              {t("placement_start_beginning")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Result screen ──
  if (phase === "result") {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-accent/20 border-2 border-accent flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-accent" />
          </div>

          <div>
            <h1 className="text-2xl font-bold mb-2">
              {t("placement_result", { level: recommendedLevel })}
            </h1>
            <p className="text-muted-foreground text-sm">
              {lang === "es"
                ? "Basado en tus respuestas, creemos que este es el mejor punto de partida para ti."
                : "Based on your answers, we think this is the best starting point for you."}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold h-12"
              onClick={() => handleStartAtLevel(recommendedLevel)}
            >
              {t("placement_start_here", { level: recommendedLevel })}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => handleStartAtLevel(1)}
            >
              {t("placement_start_beginning")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Test screen ──
  const question = QUESTIONS[currentQ];

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Progress bar */}
      <div className="p-4 pb-0">
        <Progress value={progressPercent} className="h-2" />
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {currentQ + 1} / {QUESTIONS.length}
        </p>
      </div>

      {/* Question area */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full space-y-6">
          <h2 className="text-lg font-bold text-center leading-snug">
            {question.question[lang]}
          </h2>

          <div className="space-y-3">
            {question.options.map((option, idx) => {
              const isSelected = answers[currentQ] === idx;
              return (
                <Card
                  key={idx}
                  className={cn(
                    "p-4 cursor-pointer transition-all border-2 active:scale-[0.98]",
                    isSelected
                      ? "border-secondary bg-secondary/10"
                      : "border-muted hover:border-muted-foreground/30",
                  )}
                  onClick={() =>
                    answers[currentQ] === null ? handleAnswer(idx) : undefined
                  }
                >
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isSelected && "text-secondary",
                    )}
                  >
                    {option[lang]}
                  </span>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
